/**
 * UID-59 — CSV parsing for the Cart screen's "bulk add" flow (screens.md
 * Cart §12's add-row + CSV import, and the Cart redesign's "Copy from
 * Excel" paste flow). Pure and framework-free so it is unit-testable
 * without a DOM/file-input harness.
 *
 * Two input shapes are accepted, sniffed from the first non-blank row:
 *
 *  1. NAMED-HEADER (UID-59 follow-up): if the first row looks like a header
 *     — it contains a recognizable part-number column name ("MISUMI Part
 *     Number", "Part Number", "Part #", "SKU") and/or a "Qty"/"Quantity"
 *     column — every row is mapped BY COLUMN NAME (case-insensitive, trimmed,
 *     order-independent). Only the part-number, quantity, and (as a fallback
 *     identifier only, UID-486 follow-up) part-name columns are read; every
 *     other column (Line #, Part Type, Description, Process, Material, …) is
 *     metadata and ignored. A blank/absent quantity defaults to 1. A header
 *     with no recognizable part-number column is a parse error.
 *
 *     A row whose Part Number is blank or a real export's `-`/`n/a`
 *     placeholder — a custom (non-catalog) BOM row has no MISUMI part number
 *     by definition — falls back to its Part Name (e.g. "Mounting Base") as
 *     the search identifier instead of being dropped: `sku-resolver.ts`'s
 *     `libraryLookup` already resolves a Fictiv custom Library part by exact
 *     name, so this lets that existing path find it.
 *
 *  2. POSITIONAL (the original format, unchanged): one `sku,quantity` pair per
 *     line. A literal `sku,quantity` / `sku<TAB>qty` header row is recognized
 *     case-insensitively and skipped; quantity is required here.
 *
 * Either shape may be comma-delimited (a real `.csv` upload) OR tab-delimited
 * (a block pasted straight out of Excel, which puts a literal tab between
 * cells). Every malformed row is collected as a 1-based-line-numbered error
 * rather than aborting the whole import — a typo in row 9 of a 40-row upload
 * shouldn't cost the other 39 valid rows.
 */
import { CART_CSV_MAX_ROWS, CART_MAX_LINE_QUANTITY } from './cart-limits'

export interface CartCsvRow {
  readonly sku: string
  readonly quantity: number
  /** 1-based source line number, when known (always set by `parseCartCsv`
   * itself; optional so hand-built test fixtures elsewhere don't need it).
   * Lets a later add-stage failure (`import-catalog-rows.ts`) reference "Row
   * N" too, and lets several same-reason row failures be grouped into one
   * "Rows X, Y, Z: <reason>" line instead of repeating the full message once
   * per row. */
  readonly line?: number
}

export interface CartCsvParseResult {
  readonly rows: readonly CartCsvRow[]
  readonly errors: readonly string[]
}

// Two flat alternatives (comma-spaced vs. bare-tab) rather than one pattern
// mixing `\s*` with a `[,\t]` class — `\s` already matches tab, and
// layering a quantifiable class under an adjacent `\s*` on the same
// characters is exactly the ambiguous-backtracking shape static analysis
// (and a hostile/huge pasted blob) flags. Each alternative here has a fixed,
// unambiguous structure instead.
const HEADER_PATTERN = /^sku\s*,\s*(quantity|qty)$|^sku\t(quantity|qty)$/i

/** A row's delimiter is whichever of comma/tab actually appears in it — a
 * pasted Excel block is tab-delimited throughout, a `.csv` file
 * comma-delimited throughout, so sniffing per-line (rather than assuming
 * one delimiter for the whole payload) still degrades gracefully if the
 * two are ever mixed. */
function splitRow(line: string): string[] {
  return splitDelimited(line, line.includes('\t') ? '\t' : ',')
}

/** RFC-4180-ish quoted-field split: a field wrapped in `"..."` may contain the
 * delimiter (and an escaped `""` for a literal quote) without being cut in
 * two. A real MISUMI export routinely quotes a comma-delimited text column —
 * e.g. a Part Name of `"M5, 14MM L Screw"` — and a naive `line.split(',')`
 * would break that one cell into two, shifting every column after it by one
 * (so the real Qty gets silently replaced by a fragment of the Part Name).
 * Single linear scan, no regex/backtracking risk. */
function splitDelimited(line: string, delimiter: string): string[] {
  const cells: string[] = []
  let field = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          field += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        field += ch
      }
    } else if (ch === '"' && field === '') {
      inQuotes = true
    } else if (ch === delimiter) {
      cells.push(field)
      field = ''
    } else {
      field += ch
    }
  }
  cells.push(field)
  return cells
}

/** Normalize a header cell for name matching: trim, lowercase, collapse runs
 * of whitespace to a single space (so "MISUMI  Part Number" == "misumi part
 * number"). */
function normalizeHeaderCell(cell: string): string {
  return cell.trim().toLowerCase().replace(/\s+/g, ' ')
}

/** True for a header cell naming the part-number column: "SKU", "Part #",
 * "Part No", "Part Number", or anything ending in "part number" (e.g. the
 * "MISUMI Part Number" the customer's export uses). The "MISUMI" in the label
 * does NOT restrict the source — this value feeds the same cross-source
 * resolver, which may resolve it to a MISUMI OR a Fictiv part. */
function isPartNumberHeader(cell: string): boolean {
  const c = normalizeHeaderCell(cell)
  return c === 'sku' || /part\s*(#|no\.?|number)$/.test(c)
}

/** True for a header cell naming the quantity column. */
function isQuantityHeader(cell: string): boolean {
  const c = normalizeHeaderCell(cell)
  return c === 'qty' || c === 'quantity'
}

/** True for a header cell naming the part-name column: "Part Name" or "Name".
 * Used only as a FALLBACK identifier (see `parseWithHeader`) when the row's
 * own Part Number column is empty/placeholder — a custom (non-catalog) BOM
 * row, e.g. "Mounting Base", has no MISUMI part number by definition, but
 * `sku-resolver.ts`'s `libraryLookup` already matches a Fictiv custom Library
 * part by exact NAME, so passing the name through lets that existing
 * resolution path find it instead of dropping the row outright. */
function isPartNameHeader(cell: string): boolean {
  const c = normalizeHeaderCell(cell)
  return c === 'part name' || c === 'name'
}

/** A real MISUMI export (`__fixtures__/misumi-partslist-no000066.csv`, mirrored
 * by `bom-spreadsheet-parser.ts`'s own `clean()`) fills an empty cell with a
 * literal `-`/`--`/`n/a`/`na` rather than leaving it blank — e.g. a custom
 * (non-catalog) BOM row has no MISUMI part number, so its "Part Number" cell
 * reads `-`. Without this, that dash is read as a literal SKU and fails
 * catalog lookup with a confusing "SKU '-' not found" instead of the same
 * "missing part number" treatment a truly blank cell already gets. */
function normalizeCell(cell: string): string {
  const trimmed = cell.trim()
  return ['-', '--', 'n/a', 'na'].includes(trimmed.toLowerCase()) ? '' : trimmed
}

/** Validate a parsed quantity, pushing a 1-based-line-numbered error and
 * returning `null` when it is not a positive whole number within the cap. */
function parseQuantity(quantityText: string, lineNumber: number, errors: string[]): number | null {
  const quantity = Number(quantityText)
  if (!Number.isFinite(quantity) || quantity <= 0 || !Number.isInteger(quantity)) {
    errors.push(
      `Row ${lineNumber}: quantity must be a positive whole number, got "${quantityText}"`,
    )
    return null
  }
  if (quantity > CART_MAX_LINE_QUANTITY) {
    errors.push(
      `Row ${lineNumber}: quantity ${quantity} exceeds the maximum of ${CART_MAX_LINE_QUANTITY} per line`,
    )
    return null
  }
  return quantity
}

export {
  CART_CSV_MAX_FILE_SIZE_BYTES,
  CART_CSV_MAX_ROWS,
  CART_MAX_LINE_QUANTITY,
} from './cart-limits'

export function parseCartCsv(text: string): CartCsvParseResult {
  const lines = text.split(/\r\n|\r|\n/)
  const headerIndex = lines.findIndex((line) => line.trim() !== '')
  if (headerIndex === -1) {
    return { rows: [], errors: [] }
  }

  // Sniff the first non-blank row: a NAMED header (has a part-number and/or a
  // quantity column name) switches to column-name mapping; anything else is
  // the original positional format. Split the RAW line (not trimmed) so a
  // leading empty column keeps its position.
  const headerCells = splitRow(lines[headerIndex] ?? '').map((cell) => cell.trim())
  const looksLikeHeader = headerCells.some(isPartNumberHeader) || headerCells.some(isQuantityHeader)

  return looksLikeHeader ? parseWithHeader(lines, headerIndex, headerCells) : parsePositional(lines)
}

/**
 * Column-name-mapped parse (input shape 1). Maps each row's part-number and
 * quantity cells by the header's column positions; every other column is
 * ignored. Blank/absent quantity defaults to 1.
 */
function parseWithHeader(
  lines: readonly string[],
  headerIndex: number,
  headerCells: readonly string[],
): CartCsvParseResult {
  const partNumberIndex = headerCells.findIndex(isPartNumberHeader)
  if (partNumberIndex === -1) {
    return {
      rows: [],
      errors: [
        'Couldn’t find a part-number column in the header row. Expected a column named "Part Number", "MISUMI Part Number", "Part #", or "SKU".',
      ],
    }
  }
  const quantityIndex = headerCells.findIndex(isQuantityHeader)
  const partNameIndex = headerCells.findIndex(isPartNameHeader)

  // Row cap counts every non-blank line EXCEPT the header (Security review —
  // same bound as the positional path).
  const dataLineCount = lines.filter(
    (line, index) => index !== headerIndex && line.trim() !== '',
  ).length
  if (dataLineCount > CART_CSV_MAX_ROWS) {
    return {
      rows: [],
      errors: [
        `This file has ${dataLineCount} rows, which is over the ${CART_CSV_MAX_ROWS}-row limit per import. Split it into smaller files and try again.`,
      ],
    }
  }

  const rows: CartCsvRow[] = []
  const errors: string[] = []

  lines.forEach((rawLine, index) => {
    // Skip everything up to and including the header (leading blanks included).
    if (index <= headerIndex) {
      return
    }
    const lineNumber = index + 1
    // Blank / whitespace-only rows are skipped silently (never an error) — a
    // pasted spreadsheet block routinely has them between sections.
    if (rawLine.trim() === '') {
      return
    }

    // Split the RAW line (not trimmed) so a leading empty part-number cell
    // keeps its column position; then trim each cell. Empty columns and
    // trailing delimiters (e.g. a trailing tab, or an all-empty "Description"
    // column) simply produce empty cells we don't read.
    const cells = splitRow(rawLine).map((cell) => cell.trim())

    // MORE cells than the header has is a reliable corruption signal: an
    // UNQUOTED comma inside a free-text column (e.g. a Part Name like `M5, 14MM
    // L Screw`, or a Description with several) splits that one cell into
    // several, shifting every column after it — including Qty and Part Number
    // — by an unpredictable amount. `splitDelimited` already handles a
    // PROPERLY quoted field; this is the case where the source file didn't
    // quote it at all, which is genuinely unrecoverable by column position
    // (there's no way to tell "this comma is a separator" from "this comma is
    // part of the text"). Reject with an honest diagnosis rather than silently
    // reading a text fragment as the quantity/SKU.
    if (cells.length > headerCells.length) {
      errors.push(
        `Row ${lineNumber}: found ${cells.length} columns, expected ${headerCells.length} — check for a comma inside a text field (e.g. Part Name or Description) that isn't wrapped in quotes`,
      )
      return
    }

    const rawSku = normalizeCell(cells[partNumberIndex] ?? '')
    // A custom (non-catalog) BOM row has no MISUMI part number by definition
    // — its Part Number cell is blank or the export's `-` placeholder. Rather
    // than dropping it outright, fall back to the Part Name (e.g. "Mounting
    // Base") as the search identifier: `sku-resolver.ts`'s `libraryLookup`
    // already matches a Fictiv custom Library part by exact name, so this
    // gives that existing resolution path a chance instead of forcing every
    // such row through a separate flow.
    const partName = partNameIndex === -1 ? '' : normalizeCell(cells[partNameIndex] ?? '')
    const sku = rawSku !== '' ? rawSku : partName
    if (sku === '') {
      // Neither a part number NOR a name to fall back on — the row genuinely
      // has nothing to search on. Surface it (never drop it silently) so the
      // user sees the line didn't import, consistent with the per-row
      // not-found handling downstream.
      errors.push(`Row ${lineNumber}: missing part number`)
      return
    }

    const quantityText = quantityIndex === -1 ? '' : normalizeCell(cells[quantityIndex] ?? '')
    if (quantityText === '') {
      // Blank/missing quantity → default 1 (same default the single add-line
      // box applies when its qty field is left untouched).
      rows.push({ sku, quantity: 1, line: lineNumber })
      return
    }
    const quantity = parseQuantity(quantityText, lineNumber, errors)
    if (quantity === null) {
      return
    }
    rows.push({ sku, quantity, line: lineNumber })
  })

  return { rows, errors }
}

/**
 * Original positional parse (input shape 2) — one `sku,quantity` pair per
 * line, with a literal `sku,quantity`/`sku<TAB>qty` header recognized and
 * skipped. Behavior is unchanged from before named-header support was added.
 */
function parsePositional(lines: readonly string[]): CartCsvParseResult {
  const rows: CartCsvRow[] = []
  const errors: string[] = []

  const dataLineCount = lines.filter((rawLine) => {
    const line = rawLine.trim()
    return line !== '' && !HEADER_PATTERN.test(line)
  }).length

  if (dataLineCount > CART_CSV_MAX_ROWS) {
    return {
      rows: [],
      errors: [
        `This file has ${dataLineCount} rows, which is over the ${CART_CSV_MAX_ROWS}-row limit per import. Split it into smaller files and try again.`,
      ],
    }
  }

  lines.forEach((rawLine, index) => {
    const line = rawLine.trim()
    const lineNumber = index + 1
    if (line === '') {
      return
    }
    if (HEADER_PATTERN.test(line)) {
      return
    }

    // A "Copy from Excel" paste (or a hand-typed line) sometimes carries a
    // plain space run between the SKU and quantity instead of a real tab —
    // e.g. clipboard content that lost its tabs in transit. Only safe to fall
    // back to whitespace-splitting here, in the strict 2-column shape (a SKU
    // never legitimately contains a space); the named-header path must NOT do
    // this, since a Part Name/Description cell there legitimately does.
    const parts = /[,\t]/.test(line) ? splitRow(line) : line.split(/\s+/)
    if (parts.length !== 2) {
      errors.push(`Row ${lineNumber}: expected "sku,quantity", got "${line}"`)
      return
    }

    const sku = normalizeCell(parts[0] ?? '')
    const quantityText = normalizeCell(parts[1] ?? '')

    if (sku === '') {
      errors.push(`Row ${lineNumber}: missing SKU`)
      return
    }

    const quantity = parseQuantity(quantityText, lineNumber, errors)
    if (quantity === null) {
      return
    }

    rows.push({ sku, quantity, line: lineNumber })
  })

  return { rows, errors }
}
